/**
 * useFormErrors
 *
 * Owns three error-recovery methods that were previously class methods on
 * FormViewer, called back from useSocketMessage via socketCallbacksRef:
 *
 *   restorePreviousValue()       — restores the previous field value and
 *                                  refocuses when calc server rejects input
 *   handleFilingStatusError(err) — restores value, advances focus, shows snackbar
 *   onUpdateFormError(data)      — highlights the errored field and shows snackbar
 *
 * Returns:
 *   { restorePreviousValue, handleFilingStatusError, onUpdateFormError }
 */
import { useCallback } from 'react';

const useFormErrors = ({
  fieldEventsRef, // kept-current ref to useFormFieldEvents result
  snackbarsRef, // kept-current ref to useSnackbars result
}) => {
  /**
   * Restores the field that was focused before the failed calc back to its
   * previous value and re-focuses it.
   */
  const restorePreviousValue = useCallback(() => {
    const fieldEvents = fieldEventsRef.current;
    const id = fieldEvents?.previousElRef?.current?.id;
    if (!id) return;
    const preVal = fieldEvents.valueMapRef?.current?.[id]?.value;
    if (preVal === undefined) return;
    const prevEl = document.getElementById(id);
    if (!prevEl) return;
    prevEl.value = preVal;
    prevEl.focus();
  }, [fieldEventsRef]);

  /**
   * Handles a duplicate-filing-status error from the backend: restores the
   * previous value, advances focus, then shows an error snackbar after a
   * short delay.
   *
   * @param {string} error  error message from the server
   */
  const handleFilingStatusError = useCallback(
    error => {
      restorePreviousValue();
      const fieldEvents = fieldEventsRef.current;
      fieldEvents.switchFocusToNext(fieldEvents.previousElRef.current);
      setTimeout(() => {
        snackbarsRef.current.showErrorSnackbar(error);
      }, 250);
    },
    [fieldEventsRef, restorePreviousValue, snackbarsRef],
  );

  /**
   * Highlights the errored field in red and shows the error snackbar when
   * `update_form` returns a field-level error.
   *
   * @param {{ initialField: string, error?: string }} data
   */
  const onUpdateFormError = useCallback(
    data => {
      const fieldEvents = fieldEventsRef.current;
      const field = data.initialField;
      const element = document.getElementById(field);
      const previousFieldState = fieldEvents.valueMapRef.current[field];

      if (!element || !previousFieldState) {
        if (data.error) {
          snackbarsRef.current.showErrorSnackbar(data.error);
        }
        return;
      }

      const pval = previousFieldState.value;
      const pNextField = previousFieldState.nxtFld;
      element.value = pval;
      element.focus();
      element.classList.add('error');
      fieldEvents.updateFieldValueCache(field, pval, pNextField);
      if (data.error) {
        snackbarsRef.current.showErrorSnackbar(data.error);
      }
    },
    [fieldEventsRef, snackbarsRef],
  );

  return {
    restorePreviousValue,
    handleFilingStatusError,
    onUpdateFormError,
  };
};

export default useFormErrors;
